/**
 * R11 Kennzahlen: Kz 81/86/48 = Math.trunc(Σ Netto-Cent / 100) (Abschneiden Richtung Null, negativ
 * zulässig); steuerXXElster = round2(Kz × Satz) aus der gerundeten BMG; steuerXXCent = round2(Σ Netto × Satz)
 * centgenau (T14: 570,14 vs. 570,00); Kz 66 centgenau; Kz 39 nur MONAT + M12 + Dauerfrist;
 * kz83 = steuer81Elster + steuer86Elster − kz66 − kz39.
 *
 * § 19-Monitor: Vorjahr ≤ 25.000 €, laufend ≤ 100.000 €, § 4 Nr. 12-Umsätze zählen nicht (T16).
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
import {createIssue} from './issues';
import {setupDeepLink} from './line';
import {isTaxableCategory} from './resolve-vat-category';
import {round2, sumCents, truncEuro} from './rounding';
import {LandlordTaxProfile, Monitor19, Monitor19Input, VatEngineRange, VatIssue, VatKennzahlen, VatKz, VatLine, VatRule, VatTaxStatus} from './types';

export const KZ_RELEVANT_RULES: ReadonlySet<VatRule> = new Set<VatRule>([
    'SOLL', 'IST', 'MINDEST_IST', 'MINDEST_IST_ABZUG', 'MINDERUNG_17', 'VERRECHNUNG', 'VORSTEUER', 'ADJUSTMENT',
]);

const relevant = (lines: VatLine[], kz: VatKz): VatLine[] => lines.filter(l => l.kz === kz && KZ_RELEVANT_RULES.has(l.rule));
const netCents = (lines: VatLine[], kz: VatKz): number => sumCents(relevant(lines, kz).map(l => l.net));
const taxCents = (lines: VatLine[], kz: VatKz): number => sumCents(relevant(lines, kz).map(l => l.tax));

export const computeKennzahlen = (
    lines: VatLine[],
    range: VatEngineRange,
    profile: Pick<LandlordTaxProfile, 'dauerfristverlaengerung' | 'sondervorauszahlung'>
): VatKennzahlen => {
    const net81 = netCents(lines, '81');
    const net86 = netCents(lines, '86');
    const net48 = netCents(lines, '48');

    const kz81 = truncEuro(net81);
    const kz86 = truncEuro(net86);
    const kz48 = truncEuro(net48);
    const steuer81Elster = round2(kz81 * 0.19);
    const steuer86Elster = round2(kz86 * 0.07);
    const steuer81Cent = round2((net81 / 100) * 0.19);
    const steuer86Cent = round2((net86 / 100) * 0.07);
    const kz66 = round2(taxCents(lines, '66') / 100);

    const kz39 = range.periodType === 'MONAT' && range.periodKey === 'M12' && profile.dauerfristverlaengerung
        ? round2(profile.sondervorauszahlung?.[String(range.year)] ?? 0)
        : 0;
    const kz83 = round2(steuer81Elster + steuer86Elster - kz66 - kz39);

    return {kz81, kz86, kz48, kz66, kz39, kz83, steuer81Elster, steuer86Elster, steuer81Cent, steuer86Cent};
};

export const buildKennzahlenIssues = (kennzahlen: VatKennzahlen): VatIssue[] =>
    (['kz81', 'kz86', 'kz48', 'kz66'] as const)
        .filter(key => kennzahlen[key] < 0)
        .map(key => createIssue('NEGATIVE_KZ', key,
            `Die Kennzahl ${key.replace('kz', 'Kz ')} ist negativ (${kennzahlen[key]}). Das ist zulässig (Minderungen, Rücklastschriften), bitte aber auf Plausibilität prüfen.`));

export const KU_LIMIT_VORJAHR = 25000;
export const KU_LIMIT_LAUFEND = 100000;
export const KU_NAHE_FAKTOR = 0.8;

const MONITOR_RULES: ReadonlySet<VatRule> = new Set<VatRule>(['SOLL', 'KU_ALTUMSATZ', 'MINDERUNG_17']);

export const taxableTurnoverInLines = (lines: VatLine[]): number =>
    round2(lines
        .filter(l => l.source.type === 'BOOKING' && MONITOR_RULES.has(l.rule) && isTaxableCategory(l.category))
        .reduce((sum, l) => sum + l.gross, 0));

export const computeMonitor19 = (lines: VatLine[], statusAtRangeStart: VatTaxStatus, monitorInput: Monitor19Input | null): Monitor19 | null => {
    if (statusAtRangeStart === 'REGELBESTEUERUNG' || !monitorInput) {
        return null;
    }
    const vorjahr = round2(monitorInput.vorjahr);
    const laufend = round2(monitorInput.laufendVorRange + taxableTurnoverInLines(lines));
    const state: Monitor19['state'] = vorjahr > KU_LIMIT_VORJAHR || laufend > KU_LIMIT_LAUFEND
        ? 'UEBERSCHRITTEN'
        : vorjahr >= KU_LIMIT_VORJAHR * KU_NAHE_FAKTOR || laufend >= KU_LIMIT_LAUFEND * KU_NAHE_FAKTOR
            ? 'NAHE'
            : 'OK';
    return {vorjahr, laufend, state};
};

export const buildMonitorIssues = (monitor: Monitor19 | null, statusAtRangeStart: VatTaxStatus, landlordID: string): VatIssue[] => {
    if (!monitor || statusAtRangeStart !== 'KLEINUNTERNEHMER') {
        return [];
    }
    if (monitor.state === 'UEBERSCHRITTEN') {
        return [createIssue('KU_LIMIT_EXCEEDED', landlordID,
            `Die Kleinunternehmergrenze ist überschritten (Vorjahr ${monitor.vorjahr} €, laufend ${monitor.laufend} €; Grenzen ${KU_LIMIT_VORJAHR} € / ${KU_LIMIT_LAUFEND} €). Ab dem überschreitenden Umsatz gilt die Regelbesteuerung – bitte Steuerprofil mit der Steuerberatung anpassen.`,
            {deepLink: setupDeepLink(landlordID)})];
    }
    if (monitor.state === 'NAHE') {
        return [createIssue('KU_LIMIT_NEAR', landlordID,
            `Die Kleinunternehmergrenze ist zu mehr als 80 % ausgeschöpft (Vorjahr ${monitor.vorjahr} €, laufend ${monitor.laufend} €). Bitte die weiteren steuerpflichtigen Umsätze im Blick behalten.`)];
    }
    return [];
};
